import React, { useState, useEffect } from 'react';
import { Button, Card, Row, Col, Table, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FieldArray } from 'formik';
import classNames from 'classnames';
import { last, sumBy } from 'lodash';
import Plus from 'mdi-react/PlusIcon';
import Minus from 'mdi-react/CloseIcon';
import InfoCircleOutlineIcon from 'mdi-react/InfoCircleOutlineIcon';
import AlertCircleOutline from 'mdi-react/AlertCircleOutlineIcon';

import { RolesMode } from 'entities/Roles';
import WeightBtn from 'modules/common/WeightBtn';
import { useModal } from '../hooks';
import { Input } from '../Forms';
import YesNoModal from '../YesNoModal';

import styles from './UnloadingData.module.scss';

type Props = {
  mode: RolesMode;
  handleChange: (e: React.ChangeEvent<any>) => void;
  values: any;
  errors: any;
};

type TTN = {
  weight: number;
  number: number;
  provider: string;
};

const MAX_LINES = 30;

const UnloadingData: React.FC<Props> = ({ handleChange, values, errors, mode }) => {
  const isSecurity = mode === 'security';
  const [removeIndex, setRemoveIndex] = useState<number>(0);
  const [sum, setSum] = useState<number>(0);
  const [diff, setDiff] = useState<number>(0);
  const [unloaded, setUnloaded] = useState<number>(0);
  const [ordersLine, setOrdersLine] = useState<number>(0);
  const { isShow, openModal, closeModal } = useModal();
  const { packingLists, departureVehicleWeight, entryVehicleWeight } = values;
  const isTtns = packingLists ? packingLists.length : null;

  useEffect(() => {
    const calSum = sumBy(packingLists, 'weight');
    const fixedNum = Math.floor(calSum * 100) / 100;
    setSum(fixedNum);

    if (packingLists && departureVehicleWeight) {
      const weigth = entryVehicleWeight - departureVehicleWeight;
      setUnloaded(weigth >= 0 ? weigth : 0);
    }
  }, [packingLists, departureVehicleWeight, entryVehicleWeight]);

  useEffect(() => {
    if (departureVehicleWeight && sum) {
      const fixed = Math.floor((entryVehicleWeight - departureVehicleWeight - sum) * 100) / 100;

      setDiff(fixed);
    }
  }, [sum, departureVehicleWeight]);

  const isError = (errors && errors.packingLists) || (errors && errors.departureVehicleWeight);

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex py-0">
        {isError && (
          <span className="d-flex align-items-center justify-content-center error-circle">
            <AlertCircleOutline size={15} className="text-danger" />
          </span>
        )}
        <span className="d-block font-weight-bolder ml-4 mr-5 py-1">Дані виїзду</span>
      </Card.Header>

      <Card.Body>
        <Row>
          <Col xs={8}>
            <FieldArray
              name="packingLists"
              render={(arrayHelpers) => {
                const addHandler = () => {
                  const lastEl = last(packingLists) as TTN;
                  arrayHelpers.push({ number: '', weight: '', provider: lastEl ? lastEl.provider : '' });
                  setOrdersLine((prev) => prev + 1);
                };

                const deleteHandler = () => {
                  arrayHelpers.remove(removeIndex);
                  setOrdersLine((prev) => prev - 1);
                  closeModal();
                };

                return (
                  <>
                    <Table size="sm" className={styles.table}>
                      <thead>
                        <tr>
                          <th className="d-flex align-items-center">
                            {ordersLine < MAX_LINES && isSecurity && (
                              <Button
                                variant="success"
                                className="d-flex justify-content-center align-items-center mr-2 p-1 rounded-circle"
                                onClick={addHandler}
                              >
                                <Plus />
                              </Button>
                            )}
                            Номер ТТН
                          </th>
                          <th>Постачальник</th>
                          <th>Вага, кг</th>
                        </tr>
                      </thead>
                      <tbody>
                        {packingLists?.map((value: any, index: number) => {
                          const openDialog = () => {
                            setRemoveIndex(index);
                            openModal();
                          };
                          return (
                            // eslint-disable-next-line
                            <tr key={index}>
                              <td
                                className={classNames('position-relative d-flex align-items-center mr-5', styles.cell)}
                              >
                                <Input
                                  type="number"
                                  className="w-100"
                                  name={`packingLists[${index}].number`}
                                  label=""
                                  onChange={handleChange}
                                  onLine
                                  step="1"
                                  pattern="\d+"
                                  maxLength={15}
                                  isPosNumber
                                />
                                <Button
                                  variant="danger"
                                  className="d-flex justify-content-center align-items-center ml-2 p-1 rounded-circle"
                                  onClick={openDialog}
                                >
                                  <Minus />
                                </Button>
                              </td>
                              <td className="position-relative">
                                <Input
                                  name={`packingLists[${index}].provider`}
                                  label=""
                                  onChange={handleChange}
                                  onLine
                                  maxLength={25}
                                />
                              </td>
                              <td className="position-relative d-flex flex-row mr-5">
                                <Input
                                  type="number"
                                  name={`packingLists[${index}].weight`}
                                  disabled={!packingLists[index].number}
                                  label=""
                                  onChange={handleChange}
                                  onLine
                                  step="1"
                                  pattern="\d+"
                                  maxLength={10}
                                />
                                <em className="text-danger px-1">*</em>
                              </td>
                            </tr>
                          );
                        })}
                        <tr>
                          <td>Сума по TTH, кг:</td>
                          <td aria-label="Empty cell" />
                          <td className="font-weight-bolder">{isTtns ? sum : ''}</td>
                        </tr>
                        <tr>
                          <td>Вага автомобіля на в&apos;їзді, кг:</td>
                          <td aria-label="Empty cell" />
                          <td className="font-weight-bolder">{entryVehicleWeight}</td>
                        </tr>
                        <tr>
                          <td className="d-flex">Вага автомобіля на виїзді, кг:</td>
                          <td aria-label="Empty cell" />
                          <td className="d-flex position-relative w-75">
                            <div className="d-flex flex-row mr-5">
                              <Input
                                type="number"
                                name="departureVehicleWeight"
                                label=""
                                onChange={handleChange}
                                onLine
                                min={0}
                                step="1"
                                pattern="\d+"
                              />
                              <em className="text-danger px-1">*</em>
                            </div>

                            <WeightBtn nameField="departureVehicleWeight" type="OUT" />
                          </td>
                        </tr>
                        <tr>
                          <td className="font-weight-bolder">Вивантажено, кг:</td>
                          <td aria-label="Empty cell" />
                          <td
                            className={classNames('font-weight-bolder', {
                              [styles.err]: unloaded < 0,
                              [styles.ok]: unloaded >= 0,
                            })}
                          >
                            {departureVehicleWeight ? unloaded : ''}
                          </td>
                        </tr>
                        {sum ? (
                          <tr>
                            <td className="font-weight-bolder">
                              Відхилення, кг:
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="tooltip-dec">Різниця між Вивантажено і Суммою по ТТН</Tooltip>}
                              >
                                <InfoCircleOutlineIcon size="16" className="ml-3 text-secondary" />
                              </OverlayTrigger>
                            </td>
                            <td aria-label="Empty cell" />
                            <td className="font-weight-bolder">{diff}</td>
                          </tr>
                        ) : null}
                      </tbody>
                    </Table>
                    <YesNoModal
                      isShow={isShow}
                      title="Видалити ТТН"
                      closeModal={closeModal}
                      acceptHandler={deleteHandler}
                    />
                  </>
                );
              }}
            />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default UnloadingData;
